import { useState } from "react";
import { compareSectorNames } from "@/lib/sectorSort";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface MasterSector { id: string; name: string; sector_group: string; display_order: number; }
interface Commentary { id: string; portfolio: number; sector_id: string; comment: string | null; previous_comment: string | null; last_updated: string; updated_by: string | null; }
interface SectorWeight { portfolio: number; sector_id: string; view_label: string; relative_weight: number; }

const VIEW_COLORS: Record<string, string> = {
  ERROR: "bg-red-600 text-white",
  UNDERWEIGHT: "text-red-700 bg-red-50",
  NEGATIVE: "text-orange-600 bg-orange-50",
  NEUTRAL: "text-muted-foreground bg-muted",
  POSITIVE: "text-green-600 bg-green-50",
  OVERWEIGHT: "text-green-800 bg-green-100",
};

export default function RSectorCommentary() {
  const queryClient = useQueryClient();
  const [selectedPortfolio, setSelectedPortfolio] = useState("1");
  const [editedComments, setEditedComments] = useState<Record<string, string>>({});

  const { data: sectors } = useQuery({
    queryKey: ["ba-master-sectors"],
    queryFn: async () => {
      const { data, error } = await supabase.from("ba_master_sectors").select("*").order("display_order");
      if (error) throw error;
      return data as MasterSector[];
    },
  });

  const portfolio = parseInt(selectedPortfolio);

  const { data: commentary } = useQuery({
    queryKey: ["ba-r-commentary", portfolio],
    queryFn: async () => {
      const { data, error } = await supabase.from("ba_r_sector_commentary").select("*").eq("portfolio", portfolio);
      if (error) throw error;
      return data as Commentary[];
    },
  });

  const { data: sectorWeights } = useQuery({
    queryKey: ["ba-r-sector-weights", portfolio],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ba_r_portfolio_sector_weights")
        .select("portfolio, sector_id, view_label, relative_weight")
        .eq("portfolio", portfolio);
      if (error) throw error;
      return data as SectorWeight[];
    },
  });

  const saveMutation = useMutation({
    mutationFn: async ({ sectorId, comment }: { sectorId: string; comment: string }) => {
      const existing = commentary?.find((c) => c.sector_id === sectorId);
      const { error } = await supabase.from("ba_r_sector_commentary").upsert({
        portfolio,
        sector_id: sectorId,
        comment,
        previous_comment: existing?.comment || null,
        last_updated: new Date().toISOString().split("T")[0],
      }, { onConflict: "portfolio,sector_id" });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ba-r-commentary", portfolio] });
      toast.success("Commentary saved");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const getCommentary = (sectorId: string) => commentary?.find((c) => c.sector_id === sectorId);
  const getWeight = (sectorId: string) => sectorWeights?.find((w) => w.sector_id === sectorId);

  const sortedSectors = [...(sectors ?? [])].sort((a, b) => compareSectorNames(a.name, b.name));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">R Sector Commentary</h1>
        <p className="text-sm text-muted-foreground">Investment rationale by sector and R portfolio</p>
      </div>

      <Tabs value={selectedPortfolio} onValueChange={setSelectedPortfolio}>
        <TabsList>
          {[1, 2, 3, 4, 5].map((p) => (
            <TabsTrigger key={p} value={String(p)}>R{p}</TabsTrigger>
          ))}
        </TabsList>

        {[1, 2, 3, 4, 5].map((p) => (
          <TabsContent key={p} value={String(p)} className="space-y-4 mt-4">
            <Card>
              <CardContent className="space-y-3 pt-6">
                {sortedSectors.map((sector) => {
                  const c = getCommentary(sector.id);
                  const w = getWeight(sector.id);
                  const key = `${portfolio}-${sector.id}`;
                  const currentText = editedComments[key] ?? c?.comment ?? "";

                  return (
                    <div key={sector.id} className="border rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{sector.name}</span>
                        {w && (
                          <Badge className={cn("text-[10px]", VIEW_COLORS[w.view_label] || VIEW_COLORS.NEUTRAL)}>
                            {w.view_label} ({Number(w.relative_weight) > 0 ? "+" : ""}{Number(w.relative_weight).toFixed(2)}%)
                          </Badge>
                        )}
                      </div>
                      <Textarea
                        value={currentText}
                        onChange={(e) => setEditedComments({ ...editedComments, [key]: e.target.value })}
                        placeholder="Enter investment rationale..."
                        className="text-xs min-h-[60px]"
                      />
                      {c?.previous_comment && (
                        <details className="text-xs text-muted-foreground">
                          <summary className="cursor-pointer">Previous comment</summary>
                          <p className="mt-1 p-2 bg-muted rounded text-xs">{c.previous_comment}</p>
                        </details>
                      )}
                      <div className="flex items-center justify-between">
                        {c?.last_updated && (
                          <span className="text-[10px] text-muted-foreground">
                            Last updated: {new Date(c.last_updated).toLocaleDateString("en-GB")}
                          </span>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() => saveMutation.mutate({ sectorId: sector.id, comment: currentText })}
                        >
                          <Save className="h-3 w-3 mr-1" /> Save
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
